import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import intl from "react-intl-universal";
import { FiChevronRight } from "react-icons/fi";
import { TbLock, TbMail, TbPhone, TbShield, TbUser } from "react-icons/tb";
import { LanguageSelector } from "../../../Components/Atoms/LanguageSelector";
import { ScreenGreeting } from "../../../Components/Molecules";
import { TwoFactorPromoBanner } from "../../../Components/Molecules/TwoFactorPromoBanner";
import { TwoFactorSettingsModal } from "../../../Components/Molecules/TwoFactorSettingsModal";
import { useFidoPilotOnboarding } from "../../../Hooks/useFidoPilotOnboarding";
import { TransactionalPasswordModal } from "./TransactionalPasswordModal";

const LOGIN_METHOD_LABELS = {
  email: "E-mail",
  sms: "SMS",
  whatsapp: "WhatsApp",
  google_authenticator: "Autenticador Google",
  fido: "Chave USB",
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0]?.[0] || "U").toUpperCase();
};

const getSecuritySecondaryText = (settings) => {
  const selected = settings?.selectedLoginMethods || [];
  const labels = selected
    .map((method) => LOGIN_METHOD_LABELS[method])
    .filter(Boolean);

  if (labels.length === 0) {
    return intl.get("profile.configureTwoFactor");
  }

  if (labels.length === 1) {
    return intl.get("profile.methodActive", { method: labels[0] });
  }

  if (labels.length === 2) {
    return intl.get("profile.twoMethodsActive", { first: labels[0], second: labels[1] });
  }

  return intl.get("profile.manyMethodsActive", {
    methods: labels.slice(0, -1).join(", "),
    last: labels[labels.length - 1],
  });
};

const ProfileField = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-4 last:border-b-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-[#1f1f1f]">
        <Icon className="h-5 w-5 text-brand-dark dark:text-brand-gold-mid" />
      </div>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
    </div>
    <p className="min-w-0 break-words pl-[52px] text-sm font-semibold text-brand-dark dark:text-gray-100 sm:max-w-[55%] sm:pl-0 sm:text-right">
      {value || "—"}
    </p>
  </div>
);

const ProfileSkeleton = () => (
  <div className="space-y-5 animate-pulse">
    <div className="app-card p-5">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-gray-100 dark:bg-brand-dark-border" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-40 rounded bg-gray-100 dark:bg-brand-dark-border" />
          <div className="h-4 w-56 rounded bg-gray-100 dark:bg-brand-dark-border" />
        </div>
      </div>
    </div>
    {[1, 2].map((section) => (
      <div key={section}>
        <div className="mb-3 h-3 w-20 rounded bg-gray-100 dark:bg-brand-dark-border" />
        <div className="app-card overflow-hidden p-4 space-y-4">
          {[1, 2, 3].map((row) => (
            <div key={row} className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gray-100 dark:bg-brand-dark-border" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 rounded bg-gray-100 dark:bg-brand-dark-border" />
                <div className="h-4 w-48 rounded bg-gray-100 dark:bg-brand-dark-border" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const DesktopAccount = ({ accountData, loading, refreshAccount }) => {
  const history = useHistory();
  const location = useLocation();
  const fidoPilot = useFidoPilotOnboarding(accountData);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [txPasswordModalOpen, setTxPasswordModalOpen] = useState(false);

  const openSecurityModal = () => setSecurityModalOpen(true);
  const closeSecurityModal = () => setSecurityModalOpen(false);
  const closeTwoFactorModal = () => {
    closeSecurityModal();
    fidoPilot.closeModal();
  };
  const openTxPasswordModal = () => setTxPasswordModalOpen(true);
  const closeTxPasswordModal = () => setTxPasswordModalOpen(false);

  useEffect(() => {
    if (location.state?.openTransactionalPassword !== true) return;
    setTxPasswordModalOpen(true);
    history.replace({ pathname: location.pathname, state: {} });
  }, [history, location.pathname, location.state]);

  if (loading) {
    return (
      <div className="profile-page w-full min-w-0 max-w-full">
        <ScreenGreeting
          title={intl.get("phrases.myProfile")}
          subTitle={intl.get("phrases.controlAndConfigForYourAccount")}
        />
        <ProfileSkeleton />
      </div>
    );
  }

  return (
    <div className="profile-page w-full min-w-0 max-w-full">
      <ScreenGreeting
        title={intl.get("phrases.myProfile")}
        subTitle={intl.get("phrases.controlAndConfigForYourAccount")}
      />

      <TwoFactorSettingsModal
        open={securityModalOpen || fidoPilot.modalOpen}
        onClose={closeTwoFactorModal}
        settings={fidoPilot.settings}
        registerFidoKey={fidoPilot.registerFidoKey}
        accountKey={fidoPilot.accountKey}
        removeFidoKey={fidoPilot.removeFidoKey}
            setPreferredLogin={fidoPilot.setPreferredLogin}
            setSelectedLoginMethods={fidoPilot.setSelectedLoginMethods}
            setPersonPhone={fidoPilot.setPersonPhone}
        configureGoogleAuthenticator={fidoPilot.configureGoogleAuthenticator}
        removeGoogleAuthenticator={fidoPilot.removeGoogleAuthenticator}
      />

      <TransactionalPasswordModal
        open={txPasswordModalOpen}
        onClose={closeTxPasswordModal}
        onSuccess={refreshAccount}
      />

      {fidoPilot.showBanner ? (
        <TwoFactorPromoBanner onOpenSettings={fidoPilot.openModal} />
      ) : null}

      <div className="app-card mb-5 overflow-hidden p-4 sm:p-5">
        <div className="flex items-center gap-4">
          <div
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white"
            style={{ background: "linear-gradient(135deg, #fcd177, #e7a73f)" }}
            aria-hidden
          >
            {getInitials(accountData?.userName)}
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-bold text-brand-dark dark:text-gray-100">
              {accountData?.userName || "—"}
            </h2>
            <p className="truncate text-sm text-gray-500 dark:text-gray-400">
              {accountData?.userEmail || "—"}
            </p>
          </div>
        </div>
      </div>

      <section className="mb-5 min-w-0">
        <p className="dashboard-section-label mb-3">{intl.get("profile.data")}</p>
        <div className="app-card overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("profile.personalInfo")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("profile.personalInfoSubtitle")}
            </p>
          </div>
          <ProfileField
            icon={TbUser}
            label={intl.get("phrases.fullName")}
            value={accountData?.userName}
          />
          <ProfileField
            icon={TbMail}
            label={intl.get("words.email")}
            value={accountData?.userEmail}
          />
          <ProfileField
            icon={TbPhone}
            label={intl.get("words.phone")}
            value={accountData?.userPhone}
          />
        </div>
      </section>

      <section className="mb-5 min-w-0">
        <p className="dashboard-section-label mb-3">{intl.get("profile.security")}</p>
        <div className="app-card overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("profile.accountProtection")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("profile.accountProtectionSubtitle")}
            </p>
          </div>
          <button
            type="button"
            className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-brand-cream/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 dark:hover:bg-white/5"
            onClick={openSecurityModal}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-[#1f1f1f]">
              <TbShield className="h-5 w-5 text-brand-dark dark:text-brand-gold-mid" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("profile.twoFactor")}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {getSecuritySecondaryText(fidoPilot.settings)}
              </p>
            </div>
            <FiChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-3 border-t border-gray-100 px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 dark:border-brand-dark-border"
            onClick={openTxPasswordModal}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-[#1f1f1f]">
              <TbLock className="h-5 w-5 text-brand-dark dark:text-brand-gold-mid" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("profile.transactionalPassword")}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {accountData?.transactionalPasswordQualified === true
                  ? intl.get("profile.transactionalPasswordActive")
                  : intl.get("profile.transactionalPasswordNotSet")}
              </p>
            </div>
            <FiChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
          </button>
        </div>
      </section>

      <section className="mb-5 min-w-0">
        <p className="dashboard-section-label mb-3">{intl.get("profile.preferences")}</p>
        <div className="app-card overflow-visible">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("words.language")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("profile.languageSubtitle")}
            </p>
          </div>
          <div className="px-4 py-4">
            <LanguageSelector
              variant="segmented"
              className="profile-page__language-segments"
              id="profile-language-select"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesktopAccount;
